import React, { useEffect, useRef, useState } from 'react';
import styles from './Profile.module.css';
import defaultAvatar from '../../assets/default-avatar.png'; 
import minoNFT from './mino-nft.png';
import blueNFT from './blue.png';
import greenNFT from './green.png';
import pinkNFT from './pink.png';
import redNFT from './red.png';
import Guide from '../../Guide/Guide';
import useStore from '../../../../hooks/useStore';
import Share from '../Share/Share';

const Profile = () => {
    const [isShareOpen, setIsShareOpen] = useState(true);
    const [hasInitialized, setHasInitialized] = useState(false);
    
    const { 
        address,
        userStatus,
        copied,
        isGuideOpen,
        formatAddress,
        copyReferralLink,
        getReferralLink,
        setGuideOpen,
        autoConnect,
        initWalletListeners,
        checkUserStatus
    } = useStore();

    const { 
        is_checked_in: hasNFT, 
        referral_count: totalReferred, 
        nft_rewards: myPoints,
        screenshot_url: screenshotUrl,
        color
    } = userStatus;

    let nftImage = minoNFT;
    if (color === 1) nftImage = blueNFT;
    else if (color === 2) nftImage = greenNFT;
    else if (color === 3) nftImage = pinkNFT;
    else if (color === 4) nftImage = redNFT;

    // Add specific copy functionality for Android devices
    const handleCopyToClipboard = async () => {
        if (!address) return;
        
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(referralLink);
                copyReferralLink(); // Only trigger UI state update
            } else {
                // Fallback method for some mobile devices
                const textArea = document.createElement('textarea');
                textArea.value = referralLink;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                textArea.style.top = '-999999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
                copyReferralLink(); // Only trigger UI state update
            }
        } catch (err) {
            console.error('Copy failed:', err);
        }
    };

    useEffect(() => {
        if (hasInitialized) return;
        
        const initWallet = async () => {
            const connectedAddress = await autoConnect();
            if (connectedAddress) {
                await checkUserStatus();
                setHasInitialized(true);
            }
        };

        initWallet();
        const listeners = initWalletListeners();
        
        return () => {
            if (listeners && typeof listeners.cleanup === 'function') {
                listeners.cleanup();
            }
        };
    }, [autoConnect, checkUserStatus, initWalletListeners, hasInitialized]);

    const handleActivate = () => {
        setGuideOpen(true);
    };

    const handleShareClick = () => {
        setIsShareOpen(true);
    };

    const handleCloseShare = () => {
        setIsShareOpen(false);
    };

    const referralLink = getReferralLink();

    return (
        <div className={styles.profileContainer}>
            <div className={styles.profileInfo}>
                <div className={styles.avatarContainer}>
                    <img 
                        src={hasNFT ? nftImage : defaultAvatar} 
                        alt="User Avatar" 
                        className={styles.avatar} 
                    />
                </div>
                <h2 className={styles.address}>{formatAddress(address)}</h2>
                {!address && (
                    <button 
                        className={styles.activateButton}
                        onClick={handleActivate}
                    >
                        Activate
                    </button>
                )}
            </div>

            <div className={styles.statsContainer}>
                <div className={styles.statItem}>
                <span className={styles.statLabel}>My Referrals</span>
                    <span className={styles.statValue}>{totalReferred}</span>
                </div>
                <div className={styles.statItem}>
                    <span className={styles.statLabel}>Tetrominos Earned</span>
                    <span className={styles.statValue}>{myPoints}</span>
                </div>
            </div>
            <div className={styles.referralSection}>
                <div className={styles.referralTitle}>
                    <span>My Referral Link</span>
                    <div className={styles.shareContainer}>
                        <button 
                            className={styles.shareButton}
                            onClick={handleShareClick}
                            disabled={!address}
                        >
                            <span className={styles.shareButtonText}>Share</span>
                            <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                height="20px" 
                                viewBox="0 -960 960 960" 
                                width="20px" 
                                fill="currentColor"
                                className={styles.shareIcon}
                            >
                                <path d="M680-80q-50 0-85-35t-35-85q0-6 3-28L282-392q-16 15-37 23.5t-45 8.5q-50 0-85-35t-35-85q0-50 35-85t85-35q24 0 45 8.5t37 23.5l281-164q-2-7-2.5-13.5T560-760q0-50 35-85t85-35q50 0 85 35t35 85q0 50-35 85t-85 35Zm0-80q17 0 28.5-11.5T720-200q0-17-11.5-28.5T680-240q-17 0-28.5 11.5T640-200q0 17 11.5 28.5T680-160ZM200-440q17 0 28.5-11.5T240-480q0-17-11.5-28.5T200-520q-17 0-28.5 11.5T160-480q0 17 11.5 28.5T200-440Zm480-280q17 0 28.5-11.5T720-760q0-17-11.5-28.5T680-800q-17 0-28.5 11.5T640-760q0 17 11.5 28.5T680-720Zm0 520ZM200-480Zm480-280Z"/>
                            </svg>
                        </button>
                    </div>
                </div>
                <div 
                    className={`${styles.referralCode} ${copied ? styles.copied : ''}`}
                    onClick={address ? handleCopyToClipboard : undefined}
                    title={address ? "Click to copy" : undefined}
                >
                    <div className={styles.codeDisplay}>
                        <span className={styles.referralLink}>
                            {address ? referralLink : "Connect wallet to get your referral link"}
                        </span>
                        {address && (
                            <svg 
                                className={styles.copyIcon}
                                width="24" 
                                height="24" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2"
                            >
                                <path d="M16 3H4v13" />
                                <path d="M8 7h13v13H8V7z" />
                            </svg>
                        )}
                    </div>
                </div>
            </div>

            <Guide 
                isOpen={isGuideOpen} 
                onClose={() => setGuideOpen(false)}
            />
            
            {isShareOpen && <Share onClose={handleCloseShare} />}
        </div>
    );
};

export default Profile;
